import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  toast,
} from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import { ApiClientError } from '@/lib/http-core';
import { COUNTRIES } from '@/lib/countries/countryCodes';
import { countryLabel } from '@/lib/countries/nationalityOptions';
import { useOrderAdminMemoMutations } from '../hooks/useOrderAdminMemos';
import {
  OrderInfoUpdateNotReadyError,
  useOrderInfoUpdateMutation,
  type OrderInfoPatch,
  type OrderInfoShippingPatch,
} from '../hooks/useOrderInfoUpdateMutation';

/** 수정 폼 값 — 주문 상세에서 읽은 현재 값으로 채운다. 배송지가 없는 주문(픽업)은 shipping 이 null. */
export interface OrderInfoValues {
  ordererName: string;
  ordererEmail: string;
  ordererMobile: string;
  shipping: {
    receiverName: string;
    countryCode: string;
    /** QA-567 해외 배송 — 수령지 도시·주/도(선택). */
    city: string;
    stateProvince: string;
    zipCode: string;
    address1: string;
    address2: string;
    receiverMobile: string;
  } | null;
  deliveryMemo: string;
}

type ShippingKey = keyof NonNullable<OrderInfoValues['shipping']>;
type OrdererKey = 'ordererName' | 'ordererEmail' | 'ordererMobile';

const ORDERER_FIELDS: ReadonlyArray<{ key: OrdererKey; label: string; required?: boolean }> = [
  { key: 'ordererName', label: '주문자 이름', required: true },
  { key: 'ordererEmail', label: '주문자 이메일' },
  { key: 'ordererMobile', label: '주문자 연락처', required: true },
];

const SHIPPING_FIELDS: ReadonlyArray<{ key: Exclude<ShippingKey, 'countryCode'>; label: string; required?: boolean }> = [
  { key: 'receiverName', label: '수령인', required: true },
  { key: 'city', label: '수령지 도시' },
  { key: 'stateProvince', label: '수령지 주/도' },
  { key: 'address1', label: '배송지(주소)', required: true },
  { key: 'address2', label: '배송지(상세 주소)' },
  { key: 'zipCode', label: '수령지 우편번호', required: true },
  { key: 'receiverMobile', label: '수령인 연락처', required: true },
];

const COUNTRY_LABEL = '수령지 국가';
const MEMO_LABEL = '배송 메모';

/** 바뀐 칸만 담은 요청 본문과 관리자 메모용 변경 줄("항목: 이전 → 이후"). */
export const diffOrderInfo = (
  before: OrderInfoValues,
  after: OrderInfoValues,
  countryName: (code: string) => string,
): { patch: OrderInfoPatch; lines: string[] } => {
  const patch: OrderInfoPatch = {};
  const lines: string[] = [];
  const show = (v: string) => (v.trim() === '' ? '(없음)' : v.trim());
  for (const f of ORDERER_FIELDS) {
    const a = before[f.key].trim();
    const b = after[f.key].trim();
    if (a !== b) {
      patch[f.key] = b;
      lines.push(`${f.label}: ${show(a)} → ${show(b)}`);
    }
  }
  if (before.shipping && after.shipping) {
    const ship: OrderInfoShippingPatch = {};
    for (const f of SHIPPING_FIELDS) {
      const a = before.shipping[f.key].trim();
      const b = after.shipping[f.key].trim();
      if (a !== b) {
        ship[f.key] = b;
        lines.push(`${f.label}: ${show(a)} → ${show(b)}`);
      }
    }
    if (before.shipping.countryCode !== after.shipping.countryCode) {
      ship.countryCode = after.shipping.countryCode;
      lines.push(`${COUNTRY_LABEL}: ${countryName(before.shipping.countryCode)} → ${countryName(after.shipping.countryCode)}`);
    }
    // QA-454 운영 400 — BE 는 저장 때마다 DB 의 수령인·주소·우편번호·수령인 연락처가 비었는지 본다. 이관 주문은 DB 칸이 비었는데
    //  화면엔 다른 칸 값(예: 해외 휴대폰)이 보여 "안 바뀜"으로 안 보내면 400 이 났다 → 배송지를 보낼 땐 필수 칸을 화면 값으로 함께.
    if (Object.keys(ship).length > 0) {
      for (const f of SHIPPING_FIELDS) {
        if (f.required && ship[f.key] === undefined) ship[f.key] = after.shipping[f.key].trim();
      }
      if (ship.countryCode === undefined && after.shipping.countryCode) ship.countryCode = after.shipping.countryCode;
      patch.shippingAddress = ship;
    }
  }
  if (before.deliveryMemo.trim() !== after.deliveryMemo.trim()) {
    patch.deliveryMemo = after.deliveryMemo.trim();
    lines.push(`${MEMO_LABEL}: ${show(before.deliveryMemo)} → ${show(after.deliveryMemo)}`);
  }
  // 주문자 이름·연락처도 BE 가 매번 비었는지 본다(같은 이유) — 바뀐 칸이 있으면 화면 값을 함께 보낸다(같은 값이면 BE 는 그대로).
  if (lines.length > 0) {
    if (patch.ordererName === undefined) patch.ordererName = after.ordererName.trim();
    if (patch.ordererMobile === undefined) patch.ordererMobile = after.ordererMobile.trim();
  }
  return { patch, lines };
};

/**
 * QA-454 — [주문 정보 수정]. 주문자(이름·이메일·연락처)·배송 정보(수령인·국가·도시·주/도·배송지·우편번호·연락처)·배송 메모를 고친다.
 * 저장하면 바뀐 내용을 주문 관리자 메모로 남긴다(누가 언제 무엇을 바꿨는지 — 메모 실패는 저장을 되돌리지 않는다).
 */
export const OrderInfoEditDialog = ({
  orderId,
  initial,
  locale,
  onClose,
}: {
  orderId: string;
  initial: OrderInfoValues;
  locale: string | undefined;
  onClose: () => void;
}) => {
  const { t } = useTranslation('orders');
  const update = useOrderInfoUpdateMutation(orderId);
  const { create: createMemo } = useOrderAdminMemoMutations(orderId);
  const [values, setValues] = useState<OrderInfoValues>(initial);
  const [error, setError] = useState<string | null>(null);

  const countryName = (code: string) => {
    const c = COUNTRIES.find((x) => x.code.toUpperCase() === code.toUpperCase());
    return c ? countryLabel(c, locale) : code || '(없음)';
  };
  const { patch, lines } = diffOrderInfo(initial, values, countryName);
  const changed = lines.length > 0;

  const setOrderer = (key: OrdererKey, v: string) => setValues((s) => ({ ...s, [key]: v }));
  const setShipping = (key: ShippingKey, v: string) =>
    setValues((s) => (s.shipping ? { ...s, shipping: { ...s.shipping, [key]: v } } : s));

  const submit = () => {
    const missing = [
      ...ORDERER_FIELDS.filter((f) => f.required && values[f.key].trim() === ''),
      ...(values.shipping ? SHIPPING_FIELDS.filter((f) => f.required && values.shipping![f.key].trim() === '') : []),
    ].map((f) => f.label);
    if (missing.length > 0) {
      setError(t('orders.infoEdit.required', { fields: missing.join(', '), defaultValue: '{{fields}}을(를) 입력해 주세요.' }));
      return;
    }
    setError(null);
    update.mutate(patch, {
      onSuccess: () => {
        toast.success(t('orders.infoEdit.saved', '주문 정보를 수정했습니다.'));
        createMemo.mutate(
          { scope: 'ORDER', category: 'ETC', content: `[주문 정보 수정]\n${lines.join('\n')}` },
          { onError: (e) => toast.error(friendlyMessage(e, '관리자 메모를 남기지 못했습니다.')) },
        );
        onClose();
      },
    });
  };

  const serverError = update.error
    ? update.error instanceof OrderInfoUpdateNotReadyError
      ? t('orders.infoEdit.notReady', '주문 정보 수정은 서버(BE) 반영 후 사용할 수 있습니다.')
      : // order #69 — 400·409 는 BE 사유(error.message)를 그대로(예: "픽업 주문은 배송지를 수정할 수 없습니다").
        update.error instanceof ApiClientError && (update.error.status === 400 || update.error.status === 409) && update.error.message
        ? update.error.message
        : friendlyMessage(update.error, t('orders.infoEdit.fail', '주문 정보를 수정하지 못했습니다.'))
    : null;

  const field = (id: string, label: string, value: string, onChange: (v: string) => void, required?: boolean) => (
    <label key={id} className="flex flex-col gap-1 text-sm">
      <span style={{ color: 'var(--content-muted)' }}>
        {label}
        {required ? <span style={{ color: 'var(--signal-danger)' }}> *</span> : null}
      </span>
      <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={`order-info-${id}`} />
    </label>
  );

  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('orders.infoEdit.title', '주문 정보 수정')}</DialogTitle>
          <DialogDescription>
            {t('orders.infoEdit.description', '바꾼 내용은 저장 후 관리자 메모에 자동으로 남습니다.')}
          </DialogDescription>
        </DialogHeader>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-[color:var(--content-strong)]">{t('orders.infoEdit.orderer', '주문자')}</h3>
          {ORDERER_FIELDS.map((f) => field(f.key, f.label, values[f.key], (v) => setOrderer(f.key, v), f.required))}
        </section>

        {values.shipping ? (
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-[color:var(--content-strong)]">{t('orders.infoEdit.shipping', '배송 정보')}</h3>
            {field('receiverName', '수령인', values.shipping.receiverName, (v) => setShipping('receiverName', v), true)}
            <label className="flex flex-col gap-1 text-sm">
              <span style={{ color: 'var(--content-muted)' }}>{COUNTRY_LABEL}</span>
              <Select
                value={values.shipping.countryCode}
                onChange={(e) => setShipping('countryCode', e.target.value)}
                data-testid="order-info-countryCode"
              >
                {values.shipping.countryCode === '' ? <option value="">-</option> : null}
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {countryLabel(c, locale)}
                  </option>
                ))}
              </Select>
            </label>
            {SHIPPING_FIELDS.filter((f) => f.key !== 'receiverName').map((f) =>
              field(f.key, f.label, values.shipping![f.key], (v) => setShipping(f.key, v), f.required),
            )}
          </section>
        ) : null}

        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: 'var(--content-muted)' }}>{MEMO_LABEL}</span>
          <textarea
            rows={3}
            maxLength={200}
            className="w-full rounded-md px-2.5 py-1.5 text-[13px] outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
            style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)', color: 'var(--content-default)' }}
            value={values.deliveryMemo}
            onChange={(e) => setValues((s) => ({ ...s, deliveryMemo: e.target.value }))}
            placeholder={t('orders.infoEdit.deliveryMemoPlaceholder', '예: 부재 시 경비실에 맡겨 주세요.')}
            data-testid="order-info-deliveryMemo"
          />
          <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {t('orders.infoEdit.deliveryMemoHint', '운송장에 함께 인쇄됩니다.')}
          </span>
        </label>

        {error || serverError ? (
          <p className="text-[12px]" style={{ color: 'var(--signal-danger)' }} role="alert" data-testid="order-info-error">
            {error ?? serverError}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            {t('orders.actions.cancel', '취소')}
          </Button>
          <Button type="button" size="sm" disabled={!changed || update.isPending} onClick={submit} data-testid="order-info-save">
            {update.isPending ? t('orders.infoEdit.saving', '저장 중…') : t('orders.infoEdit.save', '저장')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
